import { Box, Stack } from "@mui/material";
import { useCallback, useState } from "react";

import { PullToRefresh } from "../../lib/PullToRefresh/PullToRefresh";
import { useDebounce } from "../../lib/utils/useDebounce";
import { type ChatChannelCustomTypesFilter } from "../types";
import { useDeprecatedChatChannelStore } from "../useDeprecatedChatChannelStore";
import { ChatListContent } from "./Content";
import { ChatListHeader } from "./Header";
import { useGetChatChannels } from "./hooks/useGetChatChannels";

export function ChatListPage() {
  const [filterType, setFilterType] = useState<ChatChannelCustomTypesFilter>("all");
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearch = useDebounce({ value: searchValue, debounceTimeInMs: 300 });
  const { channels, setChannels } = useDeprecatedChatChannelStore();

  const { refetch, isFetching: isFetchingChannels } = useGetChatChannels({
    searchInput: debouncedSearch,
    customTypesFilter: filterType,
    onSuccess: (data) => {
      void setChannels(data.channels);
    },
    enabled: filterType !== "support",
  });

  const refetchChannels = useCallback(async () => {
    if (filterType === "support") {
      await setChannels([]);
      return;
    }

    await refetch();
  }, [filterType, refetch, setChannels]);

  return (
    <Stack height="100%">
      <ChatListHeader
        searchValue={searchValue}
        filterType={filterType}
        onSearchChange={setSearchValue}
        onFilterTypeChange={setFilterType}
      />

      <Box sx={{ flex: 1, overflow: "auto" }}>
        <PullToRefresh onRefresh={refetchChannels} />

        <Box sx={{ padding: 5, paddingTop: 3 }}>
          <ChatListContent
            channels={channels}
            isFetchingChannels={isFetchingChannels}
            filterType={filterType}
            onFilterTypeChange={setFilterType}
          />
        </Box>
      </Box>
    </Stack>
  );
}
